// Workaround temporal para CORS con Firebase Storage en desarrollo
import { storage } from '../firebaseconfig.js'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

class DevStorageWorkaround {
  
  // Método alternativo que intenta simular la subida usando data URLs
  async uploadAsDataUrl(file, path) {
    console.log('🔄 Usando workaround de desarrollo para:', file.name)
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = function(event) {
        const dataUrl = event.target.result
        
        // Simular un resultado de subida
        const mockResult = {
          url: dataUrl, // Usar data URL temporal
          path: path,
          name: file.name,
          size: file.size,
          type: file.type,
          isDevelopmentMock: true
        }
        
        console.log('✅ Archivo "subido" como data URL (desarrollo)')
        resolve(mockResult)
      }
      
      reader.onerror = function(error) {
        reject(new Error('Error leyendo archivo: ' + error.message))
      }
      
      reader.readAsDataURL(file)
    })
  }

  // Método que intenta subida real con timeout más corto
  async uploadWithTimeout(file, path, timeoutMs = 5000) {
    console.log('⏱️ Intentando subida con timeout de', timeoutMs, 'ms')
    
    const storageRef = ref(storage, path)
    
    const uploadPromise = uploadBytes(storageRef, file).then(async (snapshot) => {
      const downloadURL = await getDownloadURL(snapshot.ref)
      return {
        url: downloadURL,
        path: path,
        name: file.name,
        size: file.size,
        type: file.type
      }
    })
    
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Timeout de subida')), timeoutMs)
    })
    
    return Promise.race([uploadPromise, timeoutPromise])
  }

  // Método principal simplificado - solo usa data URL que funciona
  async uploadFile(file, path) {
    console.log('🔧 Usando método de desarrollo para:', file.name)
    
    // Validar que el archivo sea válido
    if (!file || file.size === 0) {
      throw new Error('Archivo inválido o vacío')
    }

    // Extraer nombre sin timestamp para conservar el original
    const nombreOriginal = file.name
    const extensionMatch = nombreOriginal.match(/\.[^.]+$/) // Obtener extensión
    const extension = extensionMatch ? extensionMatch[0] : ''
    
    return new Promise((resolve, reject) => {
      // Para PDFs y documentos grandes, usar Blob en lugar de Data URL
      // para evitar problemas de tamaño
      if (file.type === 'application/pdf' || file.size > 5 * 1024 * 1024) {
        console.log(`📄 Archivo PDF/grande detectado: ${nombreOriginal}`)
        
        const reader = new FileReader()
        
        reader.onload = function(event) {
          const arrayBuffer = event.target.result
          const blob = new Blob([arrayBuffer], { type: file.type })
          
          // Crear URL de blob que es más eficiente para archivos grandes
          const blobUrl = URL.createObjectURL(blob)
          
          const result = {
            url: blobUrl, // URL de blob en lugar de data URL
            path: path,
            nombre: nombreOriginal, // ⭐ IMPORTANTE: nombre original completo
            nombre_original: nombreOriginal,
            name: nombreOriginal,
            size: file.size,
            tamano: file.size, // ⭐ IMPORTANTE: incluir tamano
            type: file.type,
            tipo: file.type, // ⭐ IMPORTANTE: incluir tipo
            isDevelopmentMock: true,
            timestamp: Date.now(),
            extension: extension
          }
          
          console.log(`✅ PDF procesado exitosamente: ${nombreOriginal}`)
          resolve(result)
        }
        
        reader.onerror = function(error) {
          console.error('❌ Error leyendo PDF:', error)
          reject(new Error('Error leyendo archivo: ' + error.message))
        }
        
        reader.readAsArrayBuffer(file)
      } else {
        // Para imágenes y archivos pequeños, usar data URL
        console.log(`🖼️ Archivo de imagen/pequeño detectado: ${nombreOriginal}`)
        
        const reader = new FileReader()
        
        reader.onload = function(event) {
          const dataUrl = event.target.result
          
          // Crear resultado que preserve el nombre original
          const result = {
            url: dataUrl, // Data URL para imágenes
            path: path,
            nombre: nombreOriginal, // ⭐ IMPORTANTE: nombre original completo
            nombre_original: nombreOriginal,
            name: nombreOriginal,
            size: file.size,
            tamano: file.size, // ⭐ IMPORTANTE: incluir tamano
            type: file.type,
            tipo: file.type, // ⭐ IMPORTANTE: incluir tipo
            isDevelopmentMock: true,
            timestamp: Date.now(),
            extension: extension
          }
          
          console.log(`✅ Imagen procesada exitosamente: ${nombreOriginal}`)
          resolve(result)
        }
        
        reader.onerror = function(error) {
          console.error('❌ Error leyendo archivo:', error)
          reject(new Error('Error leyendo archivo: ' + error.message))
        }
        
        reader.readAsDataURL(file)
      }
    })
  }
}

export default new DevStorageWorkaround()
